import { Link, useParams } from 'react-router-dom';

import { Timeline } from '@/components/Timeline';
import { Card, Kpi, Loaded, RangeSeg } from '@/components/ui';
import { useApi, useMeta } from '@/lib/api';
import { cx } from '@/lib/cx';
import { fmtAgo, fmtInt, fmtMoney, fmtPct, initialsOf } from '@/lib/format';
import { RANGES, useParam } from '@/lib/params';
import type { UserProfile } from '@/lib/types';

export function UserPage() {
  const { userId } = useParams();
  const meta = useMeta();
  const [range] = useParam('range', '30d');
  const query = useApi<UserProfile>(`/users/${userId}`, { range });
  const now = meta.data?.now ?? new Date().toISOString();
  return (
    <div className="page">
      <div className="muted" style={{ marginBottom: 10 }}>
        <Link className="link" to="/accounts?view=users">
          Accounts
        </Link>{' '}
        / {query.data?.user.name ?? `#${userId}`}
      </div>
      <Loaded query={query}>
        {(u) => (
          <>
            <div className="ph">
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <span className="uav" style={{ width: 44, height: 44, fontSize: 16 }}>
                  {initialsOf(u.user.name ?? u.user.email)}
                </span>
                <div>
                  <div className="h1">{u.user.name ?? u.user.email}</div>
                  <div className="muted">
                    {u.user.email}
                    {u.companies.map((c) => (
                      <span key={c.id}>
                        {' · '}
                        <Link className="link" to={`/accounts/${c.id}`}>
                          {c.name}
                        </Link>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="filters">
                <RangeSeg options={RANGES.standard} fallback="30d" />
              </div>
            </div>
            <div className="kpis k4">
              <Kpi label="Asks" value={fmtInt(u.kpis.asks)} footer={<span />} />
              <Kpi label="Not-ok rate" value={fmtPct(u.kpis.not_ok_rate)} footer={<span />} />
              <Kpi label="AI spend" value={fmtMoney(u.kpis.spend)} footer={<span />} />
              <Kpi
                label="Last seen"
                value={
                  <span style={{ fontSize: 22 }}>
                    {u.kpis.last_seen_at ? `${fmtAgo(u.kpis.last_seen_at, now)} ago` : '–'}
                  </span>
                }
                footer={<span />}
              />
            </div>
            <div className="prof">
              <Card title="Timeline">
                <Timeline
                  params={{ user_id: String(u.user.id), customers: 'false', range, show: 'asks,mcp,actions' }}
                />
              </Card>
              <Card title="Features used">
                <div className="agrid">
                  {u.areas.map((a) => (
                    <div key={a.key} className={cx(a.asks > 0 && 'u')}>
                      <b>{a.asks > 0 ? fmtInt(a.asks) : <span className="faint">–</span>}</b>
                      {a.short}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </>
        )}
      </Loaded>
    </div>
  );
}
